import { keptWords, titleCase, type ProcessDraftDto } from '@nsb/core';
import { useState } from 'react';
import { Link } from 'react-router';
import { errorText } from '../../api.ts';
import { useFeedbackRecord } from '../../components/feedback-context.ts';
import { ProcessEditor } from '../../components/process/ProcessEditor.tsx';
import { PhaseList } from '../../components/process/PhaseList.tsx';
import { processAction, useDraftedProcess, useJobProcess, useProcessModel } from '../../components/process/useProcess.ts';
import { Button, Confirm, ErrorLine, LoadError, Loading, Page, Title } from '../../components/ui.tsx';
import { ProjectCrumb } from './common.tsx';
import { useProject, useProjectId } from './hooks.ts';
import { dateWords } from '../../days.ts';

/**
 * A job's process, run (Jason, 2026-10-07: "all the running and work within the process seems like it needs to happen
 * outside of the setting up ... the build level process that the client and admin/gc sees should have the job state"):
 * the steps it has, drawn as every tree is, each with where it stands, and every step's state, date and people. What it
 * has is set up in the project's Settings (`ProcessSetup`). A job kept as it ended (completed or archived, L-19 A) says so
 * here and on its setup page, in the one set of words (core `keptWords`).
 */
export function Process() {
  const projectId = useProjectId();
  const { data: project } = useProject(projectId);
  const { data, error } = useJobProcess(projectId);
  useFeedbackRecord(projectId, project ? { type: 'process', id: projectId, label: `${project.name} — process` } : null);
  if (error) return <LoadError error={error} />;
  if (!data) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  return (
    <Page full>
      <ProjectCrumb project={project} />
      <Title kicker={data.kind === 'internal' ? 'Process · an internal project' : 'Process · a client project'}>The job’s process</Title>
      {data.kept ? (
        <p className="-mt-3 mb-6 max-w-measure text-ink-2">{keptWords(data.kept, data.tree).page}</p>
      ) : (
        <p className="-mt-3 mb-6 max-w-measure text-ink-2">
          {data.kind ? 'Where each step stands.' : 'This job’s process is not started yet.'} What it has is{' '}
          <Link className="link" to={`/p/${projectId}/settings/process`}>
            set up in its settings
          </Link>
          .
        </p>
      )}
      {data.kind ? <ProcessEditor projectId={projectId} dto={data} setup={false} /> : null}
    </Page>
  );
}

/**
 * Setting a job's process up (tech-tree spec v3 §8; Jason, 2026-10-07: "defining the process from the master for a
 * project is something done in settings ... in setup youd want to see all the nodes ungreeyed and full"): the project's
 * Settings, Process. The steps it has from the master tree and its own, drawn full, its steps left out as dots; nothing
 * it has not got is drawn (design study 31, slice 2). What is added, left out or redrawn here waits in the job's draft
 * until Save the changes.
 */
export function ProcessSetup() {
  const projectId = useProjectId();
  const { data: project } = useProject(projectId);
  // The job with its draft on it: what is added, left out or redrawn here waits until Save the changes.
  const { data, error } = useDraftedProcess(projectId);
  const model = useProcessModel(data);
  const [restart, setRestart] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useFeedbackRecord(projectId, project ? { type: 'process', id: projectId, label: `${project.name} — process setup` } : null);
  if (error) return <LoadError error={error} />;
  if (!data) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  // Starting it (a job with no process yet), or starting over: the same kind of project as before.
  const start = async () => {
    setErr(null);
    try {
      await processAction(projectId, '/configure', { kind: data.kind ?? project?.processKind ?? 'client' });
      setRestart(false);
    } catch (e) {
      setErr(errorText(e));
    }
  };
  // Where every job starts: the process tree's start (Project setup).
  const startTitle = data.tree.nodes.find((n) => n.key === data.tree.start)?.title;
  const first = startTitle ? titleCase(startTitle) : 'its start';
  if (!data.kind || restart) {
    return (
      <Page>
        <ProjectCrumb project={project} />
        <Title kicker="Process">The job’s process</Title>
        <p className="mb-6 max-w-2xl text-ink-2">
          {restart
            ? `Starting over puts the process back to ${first} alone. It is allowed until anything has happened on it.`
            : `Every job starts on ${first}, and unfolds from there as far as it needs, for the life of the job.`}
        </p>
        <div className="flex flex-wrap gap-5">
          <Button onClick={() => void start()}>{restart ? 'Start over' : `Start it on ${first}`}</Button>
          {restart ? (
            <Button tone="quiet" onClick={() => setRestart(false)}>
              Keep the process as it is
            </Button>
          ) : null}
        </div>
        <ErrorLine error={err} />
      </Page>
    );
  }
  const fresh = !data.kept && data.steps.every((s) => !s.hasHistory);
  return (
    <Page full>
      <ProjectCrumb project={project} />
      <Title kicker="Settings · Process">Set up the job’s process</Title>
      {data.kept ? (
        // Kept as it ended (L-19 A): it says so, and offers nothing.
        <p className="-mt-3 mb-6 max-w-measure text-ink-2">{keptWords(data.kept, data.tree).page}</p>
      ) : (
        <p className="-mt-3 mb-6 max-w-measure text-ink-2">
          Tick the steps this job needs in each phase’s list: a step ticked shows what it needs that the job hasn’t got before anything is added. The drawing below is the job as it stands, each step it left out a dot. Find a step finds any step of the tree. Where each step stands, and working on it, is on{' '}
          <Link className="link" to={`/p/${projectId}`}>
            the project’s overview
          </Link>
          .
        </p>
      )}
      {/* Where its steps come from (Jason, 2026-10-07: "show in the project tree setup area some sort of version"). */}
      <p className="-mt-3 mb-6 max-w-measure text-sm text-ink-2">
        From the{' '}
        <Link className="link" to="/settings/process">
          master process tree
        </Link>
        , version <span className="num">{data.treeVersion}</span>
        {data.treeSavedAt ? `, saved ${dateWords(data.treeSavedAt)}` : ''}. A change saved to the master reaches this job’s steps, except what is set on a step for this job alone
        {Object.keys(data.overrides).length ? (
          <>
            {' '}(<span className="num">{Object.values(data.overrides).reduce((n, o) => n + Object.keys(o).length, 0)}</span> set here)
          </>
        ) : null}
        .
      </p>
      {fresh ? (
        <p className="-mt-3 mb-6 text-sm text-ink-3">
          Nothing has happened on it yet, so it can still{' '}
          <button type="button" className="link text-ink-2 hover:text-ink" onClick={() => setRestart(true)}>
            start over
          </button>
          .
        </p>
      ) : null}
      {data.draft?.count ? <DraftBar projectId={projectId} draft={data.draft} kept={Boolean(data.kept)} /> : null}
      {model ? <PhaseList projectId={projectId} model={model} /> : null}
      <ProcessEditor projectId={projectId} dto={data} setup />
    </Page>
  );
}

/**
 * What the draft holds, above the drawing (Still to decide, A, as drawn): how many changes are not saved yet, each in
 * its words, with Save the changes and Discard. Saved, they are made together, one line on the job's timeline. On a job
 * kept as it ended (L-19 A) a draft left open is never saved, so it offers Discard alone, the API's words saying why.
 */
export function DraftBar({ projectId, draft, kept = false }: { projectId: string; draft: ProcessDraftDto; kept?: boolean }) {
  const [all, setAll] = useState(false);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const act = async (path: '/draft/save' | '/draft/discard' | '/draft/undo') => {
    setBusy(true);
    setErr(null);
    try {
      await processAction(projectId, path);
      setAsking(false);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const shown = all ? draft.changes : draft.changes.slice(-4);
  const n = `${draft.count} ${draft.count === 1 ? 'change' : 'changes'}`;
  return (
    <section aria-label="Changes not saved yet" className="mb-4 flex flex-wrap items-start justify-between gap-x-8 gap-y-3 rounded-[2px] border border-cedar bg-cedar/10 px-4 py-3">
      <div className="min-w-0 max-w-measure">
        <p className="font-medium">{n} not saved yet</p>
        <ul className="mt-1 list-disc pl-5 text-[0.9375rem]">
          {!all && draft.changes.length > shown.length ? (
            <li className="list-none -ml-5">
              <button type="button" className="link text-ink-2 hover:text-ink" onClick={() => setAll(true)}>
                {draft.changes.length - shown.length} before these
              </button>
            </li>
          ) : null}
          {shown.map((c, i) => (
            <li key={`${i}:${c}`}>{c}</li>
          ))}
        </ul>
        <p className="meta mt-1">Nothing about the job changes until they are saved, together: one line on its timeline.</p>
        {/* Something the draft relies on changed under it: the job is shown as it is until that change is taken back. */}
        <ErrorLine error={draft.stale} />
      </div>
      <div className="flex flex-wrap items-center gap-4">
        {asking ? (
          <Confirm prompt={`Discard ${n}? The job stays as it is.`} busy={busy} onYes={() => void act('/draft/discard')} onNo={() => setAsking(false)} />
        ) : (
          <>
            {kept ? null : draft.stale ? (
              <Button busy={busy} onClick={() => void act('/draft/undo')}>
                Take the last change back
              </Button>
            ) : (
              <>
                <Button busy={busy} onClick={() => void act('/draft/save')}>
                  Save the changes
                </Button>
                {/* The same three as the master tree's bar (Jason, 2026-10-07: "there should be a way to clear/cancel and undo changes"). */}
                <Button tone="quiet" busy={busy} onClick={() => void act('/draft/undo')}>
                  Undo the last change
                </Button>
              </>
            )}
            <Button tone="quiet" onClick={() => setAsking(true)}>
              Discard
            </Button>
          </>
        )}
      </div>
      <ErrorLine error={err} />
    </section>
  );
}

